<template>
  <el-select
    v-model="value"
    multiple
    collapse-tags
    collapse-tags-tooltip
    :loading="loading"
    :placeholder="t('setup.printers.table.terminal_device_uids.placeholder')"
    @change="handleChange"
  >
    <el-option
      v-for="device in devices"
      :key="device.uid"
      :label="getLabel(device)"
      :value="device.uid"
    />
  </el-select>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { useDevice } from '@/stores/device.store';
import { ISystemDevice, SystemDeviceType } from '@/types/device.type';

const { t } = useI18n();

const $props = defineProps<{
  modelValue?: string[];
  rootFormData?: { type?: string };
}>();

const $emit = defineEmits(['change', 'update:value', 'update:modelValue']);
const deviceStore = useDevice();
const value = ref($props.modelValue || []);
const allDevices = ref<ISystemDevice[]>([]);
const loading = ref(false);
const currentDeviceUid = secureStorage.getItem('device_uid') || '';

// Filter devices based on printer type: kiosk_printer targets kiosk devices, others target client devices
const devices = computed(() => {
  const targetType = $props.rootFormData?.type === 'kiosk_printer' ? 'kiosk' : SystemDeviceType.Client;
  return allDevices.value
    .filter(device => (device.type as string) === targetType && !device.deleted_at)
    .sort((a, b) => Number(b.uid === currentDeviceUid) - Number(a.uid === currentDeviceUid));
});

const getLabel = (device: ISystemDevice) => {
  if (device.uid === currentDeviceUid) return t('deviceList.currentDevice');
  return device.machine_name || device.uid;
};

const handleChange = () => {
  $emit('change', value.value);
  $emit('update:value', value.value);
  $emit('update:modelValue', value.value);
};

watch(() => $props.modelValue, (newValue) => {
  value.value = newValue || [];
});

onMounted(async () => {
  loading.value = true;
  try { allDevices.value = await deviceStore.getDevices(); }
  finally { loading.value = false; }
});
</script>
